import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import '../styles/reports.css';
import '../styles/users.css';
import { getBranding } from '../config/branding';
import { Trans, useTranslation } from 'react-i18next';
import i18n, { currentLocale } from '../i18n';

interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  status: string;
  totp_enabled: boolean;
  parent_user_id: number | null;
  regional_office_id: number | null;
  regional_office_name: string | null;
  agency_id: number | null;
  agency_name: string | null;
  created_at: string;
  last_login_at: string | null;
  advertisers: { id: string; name: string; access_role: string }[];
  clients_count?: number;
  company_name?: string | null;
  phone?: string | null;
  email_verified?: boolean;
  billing_model?: string;
}

interface Advertiser {
  id: string;
  name: string;
}

interface Office {
  id: number;
  code: string;
  name: string;
  is_hub: boolean;
  manager: { id: number; email: string; name: string } | null;
}

interface Invitation {
  id: number;
  email: string;
  role: string;
  token: string;
  expires_at: string;
  created_at: string;
  created_by_email: string;
  accepted_at: string | null;
}

// `value` is the stored role. Its name comes from the shared auth.role_* catalogue
// (the invitation screen reads the same one); badgeKey is the short form the table
// column needs. The old `description` field was never rendered, so it is gone.
const ROLES: { value: string; badgeKey?: string }[] = [
  { value: 'admin' },
  { value: 'regional_admin' },
  { value: 'manager' },
  { value: 'analyst' },
  { value: 'agency_admin', badgeKey: 'users.badgeAgencyAdmin' },
  { value: 'agency_manager', badgeKey: 'users.badgeAgencyManager' },
  { value: 'agency_buyer', badgeKey: 'users.badgeAgencyBuyer' },
  { value: 'agency_analyst', badgeKey: 'users.badgeAgencyAnalyst' },
  { value: 'smb_client', badgeKey: 'users.badgeSelfService' },
  { value: 'freelancer' },
];

// A role we have no key for still reads as the raw value rather than blank.
const roleLabel = (value: string): string => i18n.t(`auth.role_${value}`, { defaultValue: value });
const roleBadge = (value: string): string => {
  const r = ROLES.find(x => x.value === value);
  return r?.badgeKey ? i18n.t(r.badgeKey) : roleLabel(value);
};

// Roles that only admin can assign
const ADMIN_ONLY_ROLES = new Set(['admin', 'regional_admin']);

// Roles that agency_admin can create. Includes agency_admin: an agency can have
// several heads, and appointing a colleague should not require a Sense admin.
const AGENCY_ADMIN_ROLES = new Set(['agency_admin', 'agency_manager', 'agency_buyer',
                                    'agency_analyst', 'client']);

const getAllowedRoles = (currentUserRole?: string) =>
  currentUserRole === 'admin' ? ROLES
    : currentUserRole === 'agency_admin' ? ROLES.filter(r => AGENCY_ADMIN_ROLES.has(r.value))
    // A regional admin may appoint a co-admin for their own office — the office
    // is theirs to staff. Only the global admin role stays out of reach.
    : currentUserRole === 'regional_admin' ? ROLES.filter(r => r.value !== 'admin')
    : ROLES.filter(r => !ADMIN_ONLY_ROLES.has(r.value));

const STATUS_LABELS: Record<string, { labelKey: string; color: string }> = {
  active: { labelKey: 'users.stActive', color: '#2ecc71' },
  pending: { labelKey: 'users.stPending', color: '#f39c12' },
  paused: { labelKey: 'users.stBlocked', color: '#e74c3c' },  // paused treated as blocked
  blocked: { labelKey: 'users.stBlocked', color: '#e74c3c' },
  invited: { labelKey: 'users.stInvited', color: '#3498db' },
};
const statusLabel = (status: string): string => {
  const s = STATUS_LABELS[status];
  return s ? i18n.t(s.labelKey) : status;
};

// Reusable Advertiser Picker Component
interface AdvertiserPickerProps {
  advertisers: Advertiser[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
}

const AdvertiserPicker: React.FC<AdvertiserPickerProps> = ({ advertisers, selectedIds, onChange }) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');

  const filtered = advertisers.filter(adv =>
    adv.name.toLowerCase().includes(search.toLowerCase())
  );

  const selectedAdvertisers = advertisers.filter(adv => selectedIds.includes(adv.id));
  const maxVisibleChips = 5;
  const visibleChips = selectedAdvertisers.slice(0, maxVisibleChips);
  const hiddenCount = selectedAdvertisers.length - maxVisibleChips;

  const toggleAdvertiser = (id: string) => {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter(i => i !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  };

  const removeAdvertiser = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(selectedIds.filter(i => i !== id));
  };

  return (
    <div className="users-advertisers-picker">
      {/* Selected chips */}
      <div className="users-advertisers-chips">
        {selectedAdvertisers.length === 0 ? (
          <span className="users-advertisers-chips-empty">{t('users.noClientsSelected')}</span>
        ) : (
          <>
            {visibleChips.map(adv => (
              <span key={adv.id} className="users-advertiser-chip">
                {adv.name}
                <button
                  type="button"
                  className="users-chip-remove"
                  onClick={(e) => removeAdvertiser(adv.id, e)}
                >
                  <svg viewBox="0 0 24 24" fill="none">
                    <path d="M18 6L6 18M6 6l12 12" />
                  </svg>
                </button>
              </span>
            ))}
            {hiddenCount > 0 && (
              <span className="users-chips-overflow">{t('users.moreCount', { count: hiddenCount })}</span>
            )}
          </>
        )}
      </div>

      {/* Search header */}
      <div className="users-advertisers-header">
        <div className="users-search-wrapper">
          <svg viewBox="0 0 24 24" fill="none" strokeWidth="2">
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.35-4.35" />
          </svg>
          <input
            type="text"
            placeholder={t('users.searchClients')}
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="users-advertisers-search"
          />
        </div>
        <div className="users-advertisers-actions">
          <button
            type="button"
            className="users-btn-link"
            onClick={() => onChange(advertisers.map(a => a.id))}
          >
            {t('users.selectAll')}
          </button>
          <button
            type="button"
            className="users-btn-link users-btn-clear"
            onClick={() => onChange([])}
          >
            {t('users.clear')}
          </button>
        </div>
      </div>

      {/* Count */}
      <div className="users-picker-count">
        <strong>{selectedIds.length}</strong> {t('users.ofSelected', { total: advertisers.length })}
      </div>

      {/* List */}
      <div className="users-advertisers-list">
        {filtered.map(adv => (
          <div
            key={adv.id}
            className={`users-advertiser-item ${selectedIds.includes(adv.id) ? 'selected' : ''}`}
            onClick={() => toggleAdvertiser(adv.id)}
          >
            <span className="users-checkbox-custom">
              <svg viewBox="0 0 24 24" fill="none">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </span>
            <span className="users-advertiser-name">{adv.name}</span>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="users-advertisers-empty">{t('users.noClientsFound')}</div>
        )}
      </div>
    </div>
  );
};

// Office Picker Component (single-select)
interface OfficePickerProps {
  offices: Office[];
  selectedId: number | null;
  onChange: (id: number | null) => void;
  showBlockWarning?: boolean; // Only show "will be blocked" for regional_admin
  includeHub?: boolean; // Show Central Hub in the list
}

const OfficePicker: React.FC<OfficePickerProps> = ({ offices, selectedId, onChange, showBlockWarning = true, includeHub = false }) => {
  const { t } = useTranslation();
  const selectedOffice = offices.find(o => o.id === selectedId);
  // Hide the hub by default, but if it's the ONLY office (single-office
  // white-label like Dievo) fall back to showing it — otherwise there'd be
  // nothing to pick and users can never be assigned an office.
  const nonHub = offices.filter(o => !o.is_hub);
  const selectableOffices = (includeHub || nonHub.length === 0) ? offices : nonHub;

  // When there's exactly one office to choose (e.g. a regional_admin/manager
  // for whom the backend returns only their own office), there's no real choice:
  // auto-select it and render it read-only instead of an empty-looking dropdown
  // the user must still click. Works for both create and edit modals.
  const onlyOffice = selectableOffices.length === 1 ? selectableOffices[0] : null;
  useEffect(() => {
    if (onlyOffice && selectedId !== onlyOffice.id) {
      onChange(onlyOffice.id);
    }
  }, [onlyOffice, selectedId, onChange]);

  if (onlyOffice) {
    return (
      <div className="users-office-picker">
        <select className="users-office-select" value={onlyOffice.id} disabled>
          <option value={onlyOffice.id}>{onlyOffice.name} ({onlyOffice.code})</option>
        </select>
      </div>
    );
  }

  return (
    <div className="users-office-picker">
      <select
        value={selectedId || ''}
        onChange={e => onChange(e.target.value ? parseInt(e.target.value) : null)}
        className="users-office-select"
      >
        <option value="">{t('users.selectOffice')}</option>
        {selectableOffices.map(office => (
          <option key={office.id} value={office.id}>
            {office.name} ({office.code})
            {office.manager ? t('users.officeManagerSuffix', { name: office.manager.name }) : ''}
          </option>
        ))}
      </select>
      {showBlockWarning && selectedOffice && selectedOffice.manager && (
        <div className="users-office-warning">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
            <line x1="12" y1="9" x2="12" y2="13"/>
            <line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
          <span>
            <Trans
              i18nKey="users.currentManagerBlocked"
              values={{ name: selectedOffice.manager.name }}
              components={{ b: <strong /> }}
            />
          </span>
        </div>
      )}
    </div>
  );
};

const API_BASE = process.env.REACT_APP_API_URL || '';

const UsersPage: React.FC = () => {
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  const isAgencyAdmin = currentUser?.role === 'agency_admin';
  const [users, setUsers] = useState<User[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [advertisers, setAdvertisers] = useState<Advertiser[]>([]);
  const [offices, setOffices] = useState<Office[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filters
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [agencyFilter, setAgencyFilter] = useState<string>('');
  const [agencies, setAgencies] = useState<{ id: number; name: string }[]>([]);

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [showUnblockModal, setShowUnblockModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [userToBlock, setUserToBlock] = useState<User | null>(null);
  const [userToUnblock, setUserToUnblock] = useState<User | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isBlocking, setIsBlocking] = useState(false);
  const [isUnblocking, setIsUnblocking] = useState(false);

  // Close modals on Esc key
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (showCreateModal) setShowCreateModal(false);
        if (showInviteModal) setShowInviteModal(false);
        if (showEditModal) setShowEditModal(false);
        if (showDeleteModal && !isDeleting) setShowDeleteModal(false);
        if (showBlockModal && !isBlocking) setShowBlockModal(false);
        if (showUnblockModal && !isUnblocking) setShowUnblockModal(false);
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [showCreateModal, showInviteModal, showEditModal, showDeleteModal, showBlockModal, showUnblockModal, isDeleting, isBlocking, isUnblocking]);

  // Active tab
  const [activeTab, setActiveTab] = useState<'users' | 'invitations'>('users');

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('access_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    };
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (roleFilter) params.append('role', roleFilter);
      if (statusFilter) params.append('status', statusFilter);
      if (agencyFilter) params.append('agency_id', agencyFilter);

      const response = await fetch(`${API_BASE}/api/admin/users?${params}`, {
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
      } else {
        throw new Error('Failed to fetch users');
      }
    } catch (err) {
      setError(t('users.errLoadUsers'));
      console.error(err);
    }
  }, [roleFilter, statusFilter, agencyFilter, getAuthHeaders]);

  const fetchInvitations = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/users/invitations`, {
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        const data = await response.json();
        setInvitations(data.invitations || []);
      }
    } catch (err) {
      console.error('Failed to fetch invitations:', err);
    }
  }, [getAuthHeaders]);

  const fetchAdvertisers = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/advertisers`, {
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        const data = await response.json();
        // Map advertiser_id -> id, display_name -> name
        const mapped = (data.advertisers || []).map((a: { advertiser_id: string; display_name: string }) => ({
          id: a.advertiser_id,
          name: a.display_name,
        }));
        setAdvertisers(mapped);
      }
    } catch (err) {
      console.error('Failed to fetch advertisers:', err);
    }
  }, [getAuthHeaders]);

  const fetchOffices = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/regional-offices`, {
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        const data = await response.json();
        setOffices(data.offices || []);
      }
    } catch (err) {
      console.error('Failed to fetch offices:', err);
    }
  }, [getAuthHeaders]);

  const fetchAgencies = useCallback(async () => {
    if (isAgencyAdmin) return; // agency_admin doesn't need agency filter
    try {
      const response = await fetch(`${API_BASE}/api/admin/agencies?status=active`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setAgencies(data.agencies || []);
      }
    } catch (err) {
      console.error('Failed to fetch agencies:', err);
    }
  }, [getAuthHeaders, isAgencyAdmin]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchUsers(), fetchInvitations(), fetchAdvertisers(), fetchOffices(), fetchAgencies()]);
      setLoading(false);
    };
    loadData();
  }, [fetchUsers, fetchInvitations, fetchAdvertisers, fetchOffices, fetchAgencies]);

  const filteredUsers = users
    .filter(user => {
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const fullName = `${user.first_name} ${user.last_name}`.toLowerCase();
        if (!user.email.toLowerCase().includes(query) && !fullName.includes(query)) {
          return false;
        }
      }
      return true;
    })
    // Sort: active first, then pending, then paused/blocked at the end
    .sort((a, b) => {
      const statusOrder: Record<string, number> = {
        'active': 0,
        'pending': 1,
        'paused': 2,
        'blocked': 3,
      };
      return (statusOrder[a.status] ?? 4) - (statusOrder[b.status] ?? 4);
    });

  const handleApprove = async (userId: number) => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/users/${userId}/approve`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        await fetchUsers();
      } else {
        const data = await response.json();
        setError(data.detail || t('users.errApprove'));
      }
    } catch (err) {
      setError(t('users.errApprove'));
    }
  };

  const handleBlockClick = (user: User) => {
    setUserToBlock(user);
    setShowBlockModal(true);
  };

  const handleConfirmBlock = async () => {
    if (!userToBlock) return;

    setIsBlocking(true);
    try {
      const response = await fetch(`${API_BASE}/api/admin/users/${userToBlock.id}/block`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        setShowBlockModal(false);
        setUserToBlock(null);
        await fetchUsers();
      } else {
        const data = await response.json();
        setError(data.detail || t('users.errBlock'));
        setShowBlockModal(false);
      }
    } catch (err) {
      setError(t('users.errBlock'));
      setShowBlockModal(false);
    } finally {
      setIsBlocking(false);
    }
  };

  const handleUnblockClick = (user: User) => {
    setUserToUnblock(user);
    setShowUnblockModal(true);
  };

  const handleConfirmUnblock = async () => {
    if (!userToUnblock) return;

    setIsUnblocking(true);
    try {
      const response = await fetch(`${API_BASE}/api/admin/users/${userToUnblock.id}/unblock`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        setShowUnblockModal(false);
        setUserToUnblock(null);
        await fetchUsers();
      } else {
        const data = await response.json();
        setError(data.detail || t('users.errUnblock'));
        setShowUnblockModal(false);
      }
    } catch (err) {
      setError(t('users.errUnblock'));
      setShowUnblockModal(false);
    } finally {
      setIsUnblocking(false);
    }
  };

  const handleDeleteClick = (user: User) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;

    setIsDeleting(true);
    try {
      const response = await fetch(`${API_BASE}/api/admin/users/${userToDelete.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        setShowDeleteModal(false);
        setUserToDelete(null);
        await fetchUsers();
      } else {
        const data = await response.json();
        setError(data.detail || t('users.errDelete'));
        setShowDeleteModal(false);
      }
    } catch (err) {
      setError(t('users.errDelete'));
      setShowDeleteModal(false);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteInvitation = async (invitationId: number) => {
    if (!window.confirm(t('users.confirmDeleteInvitation'))) return;

    try {
      const response = await fetch(`${API_BASE}/api/admin/users/invitations/${invitationId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        await fetchInvitations();
      }
    } catch (err) {
      setError(t('users.errDeleteInvitation'));
    }
  };

  const copyInviteLink = (token: string) => {
    const link = `${window.location.origin}/auth/accept-invite?token=${token}`;
    navigator.clipboard.writeText(link);
  };

  return (
    <>
      {/* Header */}
      <div className="header">
        <div className="header-title">
          <h1>{t('users.title')}</h1>
          <p className="header-subtitle">{t('users.subtitle')}</p>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="info-box" style={{ marginBottom: '24px', borderLeftColor: '#e74c3c', background: 'rgba(231, 76, 60, 0.1)' }}>
          <strong>{t('clients.errorLabel')}</strong> {error}
          <button onClick={() => setError('')} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
        </div>
      )}

      {/* Main Layout */}
      <div className="reports-layout">
        {/* Filters Panel */}
        <aside className="filters-panel">
          {/* Tabs */}
          <div className="filter-section">
            <h3 className="section-heading">{t('users.view')}</h3>
            <div className="status-segmented" style={{ width: '100%' }}>
              <button
                className={`status-segment ${activeTab === 'users' ? 'active' : ''}`}
                onClick={() => setActiveTab('users')}
              >
                {t('users.tabUsers')} <span className="segment-count">{users.length}</span>
              </button>
              <button
                className={`status-segment ${activeTab === 'invitations' ? 'active' : ''}`}
                onClick={() => setActiveTab('invitations')}
              >
                {t('users.tabInvites')} <span className="segment-count">{invitations.filter(i => !i.accepted_at).length}</span>
              </button>
            </div>
          </div>

          {/* Search */}
          {activeTab === 'users' && (
            <>
              <div className="filter-section">
                <h3 className="section-heading">{t('common.search')}</h3>
                <div className="campaign-search-input-wrapper">
                  <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="11" cy="11" r="8"></circle>
                    <path d="m21 21-4.35-4.35"></path>
                  </svg>
                  <input
                    type="text"
                    className="campaign-search-input"
                    placeholder={t('users.searchNameEmail')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>

              <div className="filter-section">
                <h3 className="section-heading">{t('users.role')}</h3>
                <select
                  className="form-select"
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                >
                  <option value="">{t('users.allRoles')}</option>
                  {getAllowedRoles(currentUser?.role).map(role => (
                    <option key={role.value} value={role.value}>{roleLabel(role.value)}</option>
                  ))}
                </select>
              </div>

              <div className="filter-section">
                <h3 className="section-heading">{t('users.status')}</h3>
                <select
                  className="form-select"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="">{t('users.allStatuses')}</option>
                  <option value="active">{t('users.stActive')}</option>
                  <option value="pending">{t('users.stPending')}</option>
                  <option value="blocked">{t('users.stBlocked')}</option>
                </select>
              </div>

              {!isAgencyAdmin && agencies.length > 0 && (
              <div className="filter-section">
                <h3 className="section-heading">{t('clients.agency')}</h3>
                <select
                  className="form-select"
                  value={agencyFilter}
                  onChange={(e) => setAgencyFilter(e.target.value)}
                >
                  <option value="">{t('clients.allAgencies')}</option>
                  {agencies.map(agency => (
                    <option key={agency.id} value={agency.id}>{agency.name}</option>
                  ))}
                </select>
              </div>
              )}
            </>
          )}

          {/* Actions */}
          <div className="filter-section" style={{ marginTop: 'auto' }}>
            <button className="btn-primary" style={{ width: '100%', marginBottom: '12px' }} onClick={() => setShowCreateModal(true)}>
              <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: 18, height: 18, marginRight: 8 }}>
                <path d="M15 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm-9-2V7H4v3H1v2h3v3h2v-3h3v-2H6zm9 4c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
              </svg>
              {t('users.createUser')}
            </button>
            <button className="btn-secondary" style={{ width: '100%' }} onClick={() => setShowInviteModal(true)}>
              <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: 18, height: 18, marginRight: 8 }}>
                <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
              </svg>
              {t('users.sendInvite')}
            </button>
          </div>
        </aside>

        {/* Results Area */}
        <div className="results-area visible">
          {loading ? (
            <div className="users-loading">
              <div className="users-spinner" />
            </div>
          ) : activeTab === 'users' ? (
            <div className="users-table-container">
          <table className="users-table">
            <thead>
              <tr>
                <th>{t('users.colUser')}</th>
                <th>{t('users.role')}</th>
                {!isAgencyAdmin && <th>{t('users.colOffice')}</th>}
                <th>{t('users.status')}</th>
                <th>2FA</th>
                <th>{t('users.colClients')}</th>
                <th>{t('users.colLastLogin')}</th>
                <th>{t('users.colActions')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map(user => (
                <tr key={user.id} className={user.status === 'paused' || user.status === 'blocked' ? 'users-row-disabled' : ''}>
                  <td>
                    <div className="users-user-cell">
                      <div className="users-avatar">
                        {user.first_name?.[0]}{user.last_name?.[0]}
                      </div>
                      <div className="users-user-info">
                        <span className="users-user-name">
                          {user.first_name} {user.last_name}
                          {user.role === 'smb_client' && user.advertisers?.[0]?.name && (
                            <span className="clients-agency-badge" style={{ background: '#e8f4fd', color: '#1976d2' }}>{user.advertisers[0].name}</span>
                          )}
                          {user.agency_name && <span className="clients-agency-badge">{user.agency_name}</span>}
                        </span>
                        <span className="users-user-email">
                          {user.email}
                          {user.status === 'pending' && user.email_verified === false && (
                            <span title={t('users.emailNotVerified')} style={{ color: '#e74c3c', marginLeft: 4, fontSize: 11 }}>&#10007;</span>
                          )}
                          {user.status === 'pending' && user.email_verified === true && (
                            <span title={t('users.emailVerified')} style={{ color: '#2ecc71', marginLeft: 4, fontSize: 11 }}>&#10003;</span>
                          )}
                        </span>
                        {user.status === 'pending' && (user.company_name || user.phone) && (
                          <span style={{ fontSize: 11, color: 'var(--text-tertiary, #adb5bd)' }}>
                            {user.company_name && <span>{user.company_name}</span>}
                            {user.company_name && user.phone && <span> &middot; </span>}
                            {user.phone && <span>{user.phone}</span>}
                          </span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className="users-role-badge">{roleBadge(user.role)}</span>
                  </td>
                  {!isAgencyAdmin && (
                  <td>
                    <span style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>
                      {user.regional_office_name || '—'}
                    </span>
                  </td>
                  )}
                  <td>
                    <span
                      className="users-status-badge"
                      style={{ backgroundColor: STATUS_LABELS[user.status]?.color || '#6c757d' }}
                    >
                      {statusLabel(user.status)}
                    </span>
                  </td>
                  <td>
                    {user.totp_enabled ? (
                      <svg className="users-icon users-icon-success" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                        <polyline points="22 4 12 14.01 9 11.01"/>
                      </svg>
                    ) : (
                      <svg className="users-icon users-icon-muted" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"/>
                        <line x1="15" y1="9" x2="9" y2="15"/>
                        <line x1="9" y1="9" x2="15" y2="15"/>
                      </svg>
                    )}
                  </td>
                  <td>
                    <span className="users-advertisers-count">
                      {user.role === 'admin' ? (
                        <em style={{ color: 'var(--accent-orange)' }}>{t('users.allClients')}</em>
                      ) : (() => {
                        const count = user.clients_count ?? (user.advertisers || []).length;
                        return <>{t('users.clientsCount', { count })}</>;
                      })()}
                    </span>
                  </td>
                  <td>
                    {user.last_login_at ? (
                      <span className="users-date">
                        {new Date(user.last_login_at).toLocaleDateString(currentLocale())}
                      </span>
                    ) : (
                      <span className="users-date users-date-never">{t('users.never')}</span>
                    )}
                  </td>
                  <td>
                    <div className="users-actions">
                      <button
                        className="users-action-btn"
                        title={t('users.actEdit')}
                        onClick={() => {
                          setSelectedUser(user);
                          setShowEditModal(true);
                        }}
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                        </svg>
                      </button>
                      {user.status === 'pending' && (
                        <button
                          className="users-action-btn users-action-approve"
                          title={t('users.actApprove')}
                          onClick={() => handleApprove(user.id)}
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="20 6 9 17 4 12"/>
                          </svg>
                        </button>
                      )}
                      {user.status === 'active' && user.id !== currentUser?.id && (
                        <button
                          className="users-action-btn users-action-block"
                          title={t('users.actBlock')}
                          onClick={() => handleBlockClick(user)}
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"/>
                            <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/>
                          </svg>
                        </button>
                      )}
                      {(user.status === 'paused' || user.status === 'blocked') && (
                        <>
                          <button
                            className="users-action-btn users-action-unblock"
                            title={t('users.actUnblock')}
                            onClick={() => handleUnblockClick(user)}
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
                              <polyline points="10 17 15 12 10 7"/>
                              <line x1="15" y1="12" x2="3" y2="12"/>
                            </svg>
                          </button>
                          <button
                            className="users-action-btn users-action-delete"
                            title={t('users.actDelete')}
                            onClick={() => handleDeleteClick(user)}
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <circle cx="12" cy="12" r="10"/>
                              <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/>
                            </svg>
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {filteredUsers.length === 0 && (
                <tr>
                  <td colSpan={isAgencyAdmin ? 7 : 8} className="users-empty">
                    {t('users.noUsersFound')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="users-invitations">
          {invitations.filter(i => !i.accepted_at).length === 0 ? (
            <div className="users-empty-state">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 2L11 13"/>
                <path d="M22 2l-7 20-4-9-9-4 20-7z"/>
              </svg>
              <h3>{t('users.noPendingInvites')}</h3>
              <p>{t('users.sendInviteHint')}</p>
              <button className="users-btn users-btn-primary" onClick={() => setShowInviteModal(true)}>
                {t('users.sendInvite')}
              </button>
            </div>
          ) : (
            <div className="users-invitations-grid">
              {invitations.filter(i => !i.accepted_at).map(invitation => (
                <div key={invitation.id} className="users-invitation-card">
                  <div className="users-invitation-header">
                    <span className="users-invitation-email">{invitation.email}</span>
                    <span className="users-role-badge">{roleLabel(invitation.role)}</span>
                  </div>
                  <div className="users-invitation-meta">
                    <span>{t('users.sentOn', { date: new Date(invitation.created_at).toLocaleDateString(currentLocale()) })}</span>
                    <span>{t('users.expiresOn', { date: new Date(invitation.expires_at).toLocaleDateString(currentLocale()) })}</span>
                  </div>
                  <div className="users-invitation-actions">
                    <button
                      className="users-btn users-btn-small users-btn-secondary"
                      onClick={() => copyInviteLink(invitation.token)}
                    >
                      {t('users.copyLink')}
                    </button>
                    <button
                      className="users-btn users-btn-small users-btn-danger"
                      onClick={() => handleDeleteInvitation(invitation.id)}
                    >
                      {t('users.actDelete')}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
        </div>
      </div>

      {/* Create User Modal */}
      {showCreateModal && (
        <CreateUserModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            fetchUsers();
            fetchOffices();
          }}
          advertisers={advertisers}
          offices={offices}
          getAuthHeaders={getAuthHeaders}
          currentUserRole={currentUser?.role}
        />
      )}

      {/* Invite User Modal */}
      {showInviteModal && (
        <InviteUserModal
          onClose={() => setShowInviteModal(false)}
          onSuccess={() => {
            setShowInviteModal(false);
            fetchInvitations();
          }}
          advertisers={advertisers}
          offices={offices}
          getAuthHeaders={getAuthHeaders}
          currentUserRole={currentUser?.role}
        />
      )}

      {/* Edit User Modal */}
      {showEditModal && selectedUser && (
        <EditUserModal
          user={selectedUser}
          onClose={() => {
            setShowEditModal(false);
            setSelectedUser(null);
          }}
          onSuccess={() => {
            setShowEditModal(false);
            setSelectedUser(null);
            fetchUsers();
            fetchOffices();
          }}
          advertisers={advertisers}
          offices={offices}
          getAuthHeaders={getAuthHeaders}
          currentUserRole={currentUser?.role}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && userToDelete && (
        <div className="users-modal-overlay" onClick={() => !isDeleting && setShowDeleteModal(false)}>
          <div className="users-modal" style={{ maxWidth: '420px' }} onClick={(e) => e.stopPropagation()}>
            <div className="users-modal-header">
              <h2>{t('users.deleteUser')}</h2>
              <button className="users-modal-close" onClick={() => !isDeleting && setShowDeleteModal(false)}>&times;</button>
            </div>
            <div className="users-modal-body" style={{ textAlign: 'center' }}>
              <div style={{
                width: '56px',
                height: '56px',
                margin: '0 auto 20px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'rgba(220, 53, 69, 0.1)',
                color: '#dc3545'
              }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/>
                </svg>
              </div>
              <p style={{ margin: '0 0 8px', fontSize: '15px', color: 'var(--text-primary)' }}>
                {t('users.confirmDeleteUser')}
              </p>
              <p style={{ margin: '0 0 8px', fontSize: '16px', fontWeight: 600, color: 'var(--text-primary)' }}>
                "{userToDelete.first_name} {userToDelete.last_name}"?
              </p>
              <p style={{ margin: '0', fontSize: '13px', color: 'var(--text-secondary)' }}>
                {t('lists.cannotUndo')}
              </p>
            </div>
            <div className="users-modal-footer" style={{ justifyContent: 'center' }}>
              <button
                type="button"
                className="users-btn users-btn-secondary"
                onClick={() => setShowDeleteModal(false)}
                disabled={isDeleting}
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                className="users-btn users-btn-danger"
                onClick={handleConfirmDelete}
                disabled={isDeleting}
              >
                {t(isDeleting ? 'clients.deleting' : 'users.actDelete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Unblock Confirmation Modal */}
      {showUnblockModal && userToUnblock && (
        <div className="users-modal-overlay" onClick={() => !isUnblocking && setShowUnblockModal(false)}>
          <div className="users-modal" style={{ maxWidth: '420px' }} onClick={(e) => e.stopPropagation()}>
            <div className="users-modal-header">
              <h2>{t('users.unblockUser')}</h2>
              <button className="users-modal-close" onClick={() => !isUnblocking && setShowUnblockModal(false)}>&times;</button>
            </div>
            <div className="users-modal-body" style={{ textAlign: 'center' }}>
              <div style={{
                width: '56px',
                height: '56px',
                margin: '0 auto 20px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'rgba(52, 152, 219, 0.1)',
                color: '#3498db'
              }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
                  <polyline points="10 17 15 12 10 7"/>
                  <line x1="15" y1="12" x2="3" y2="12"/>
                </svg>
              </div>
              <p style={{ margin: '0 0 8px', fontSize: '15px', color: 'var(--text-primary)' }}>
                {t('users.confirmUnblockUser')}
              </p>
              <p style={{ margin: '0 0 8px', fontSize: '16px', fontWeight: 600, color: 'var(--text-primary)' }}>
                "{userToUnblock.first_name} {userToUnblock.last_name}"?
              </p>
              <p style={{ margin: '0', fontSize: '13px', color: 'var(--text-secondary)' }}>
                {t('users.unblockNote')}
              </p>
            </div>
            <div className="users-modal-footer" style={{ justifyContent: 'center' }}>
              <button
                type="button"
                className="users-btn users-btn-secondary"
                onClick={() => setShowUnblockModal(false)}
                disabled={isUnblocking}
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                className="users-btn users-btn-primary"
                onClick={handleConfirmUnblock}
                disabled={isUnblocking}
              >
                {t(isUnblocking ? 'users.unblocking' : 'users.actUnblock')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Block Confirmation Modal */}
      {showBlockModal && userToBlock && (
        <div className="users-modal-overlay" onClick={() => !isBlocking && setShowBlockModal(false)}>
          <div className="users-modal" style={{ maxWidth: '420px' }} onClick={(e) => e.stopPropagation()}>
            <div className="users-modal-header">
              <h2>{t('users.blockUser')}</h2>
              <button className="users-modal-close" onClick={() => !isBlocking && setShowBlockModal(false)}>&times;</button>
            </div>
            <div className="users-modal-body" style={{ textAlign: 'center' }}>
              <div style={{
                width: '56px',
                height: '56px',
                margin: '0 auto 20px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'rgba(231, 76, 60, 0.1)',
                color: '#e74c3c'
              }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/>
                </svg>
              </div>
              <p style={{ margin: '0 0 8px', fontSize: '15px', color: 'var(--text-primary)' }}>
                {t('users.confirmBlockUser')}
              </p>
              <p style={{ margin: '0 0 8px', fontSize: '16px', fontWeight: 600, color: 'var(--text-primary)' }}>
                "{userToBlock.first_name} {userToBlock.last_name}"?
              </p>
              <p style={{ margin: '0', fontSize: '13px', color: 'var(--text-secondary)' }}>
                {t('users.blockNote')}
              </p>
            </div>
            <div className="users-modal-footer" style={{ justifyContent: 'center' }}>
              <button
                type="button"
                className="users-btn users-btn-secondary"
                onClick={() => setShowBlockModal(false)}
                disabled={isBlocking}
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                className="users-btn users-btn-danger"
                onClick={handleConfirmBlock}
                disabled={isBlocking}
              >
                {t(isBlocking ? 'users.blocking' : 'users.actBlock')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Create User Modal
interface CreateUserModalProps {
  onClose: () => void;
  onSuccess: () => void;
  advertisers: Advertiser[];
  offices: Office[];
  getAuthHeaders: () => Record<string, string>;
  currentUserRole?: string;
}

const CreateUserModal: React.FC<CreateUserModalProps> = ({ onClose, onSuccess, advertisers, offices, getAuthHeaders, currentUserRole }) => {
  const { t } = useTranslation();
  const defaultRole = getAllowedRoles(currentUserRole)[0]?.value || 'analyst';
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    role: defaultRole,
    advertiser_ids: [] as string[],
    regional_office_id: null as number | null,
    agency_id: null as number | null,
    domain: '',
    company_name: '',
    parent_user_id: null as number | null,
  });
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [officeAdvertisers, setOfficeAdvertisers] = useState<Advertiser[]>([]);
  const [loadingAdvertisers, setLoadingAdvertisers] = useState(false);

  // Fetch advertisers filtered by office when office changes (for manager/analyst)
  useEffect(() => {
    const needsOfficeFilter = ['analyst', 'manager'].includes(formData.role);
    if (!needsOfficeFilter || !formData.regional_office_id) {
      setOfficeAdvertisers([]);
      return;
    }
    const fetchOfficeAdvertisers = async () => {
      setLoadingAdvertisers(true);
      try {
        const response = await fetch(
          `${API_BASE}/api/advertisers?regional_office_id=${formData.regional_office_id}&active_only=true`,
          { headers: getAuthHeaders() }
        );
        if (response.ok) {
          const data = await response.json();
          setOfficeAdvertisers((data.advertisers || []).map((a: any) => ({
            id: a.advertiser_id,
            name: a.display_name,
          })));
        }
      } catch (err) {
        console.error('Failed to fetch office advertisers:', err);
      } finally {
        setLoadingAdvertisers(false);
      }
    };
    fetchOfficeAdvertisers();
  }, [formData.regional_office_id, formData.role, getAuthHeaders]);

  // Close on Esc
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isSubmitting) onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose, isSubmitting]);

  const isAdminRole = formData.role === 'admin';
  const isRegionalAdmin = formData.role === 'regional_admin';
  const isSmbClient = formData.role === 'smb_client';
  const isFreelancerRole = formData.role === 'freelancer';
  const needsOffice = ['analyst', 'manager'].includes(formData.role);
  const isAgencyRole = ['agency_admin', 'agency_manager', 'agency_buyer', 'agency_analyst'].includes(formData.role);
  const isCurrentUserAgencyAdmin = currentUserRole === 'agency_admin';
  const [createAgencies, setCreateAgencies] = useState<{ id: number; name: string }[]>([]);
  const [domainTouched, setDomainTouched] = useState(false);
  const [smbManagers, setSmbManagers] = useState<{ id: number; email: string; first_name: string | null; last_name: string | null }[]>([]);

  // Fetch managers for smb_client/freelancer when office changes
  useEffect(() => {
    if ((!isSmbClient && !isFreelancerRole) || !formData.regional_office_id) {
      setSmbManagers([]);
      return;
    }
    const fetchManagers = async () => {
      try {
        const response = await fetch(
          `${API_BASE}/api/admin/clients/managers?office_id=${formData.regional_office_id}`,
          { headers: getAuthHeaders() }
        );
        if (response.ok) {
          const data = await response.json();
          setSmbManagers(data.managers || []);
        }
      } catch {}
    };
    fetchManagers();
  }, [isSmbClient, isFreelancerRole, formData.regional_office_id, getAuthHeaders]);

  // Fetch agencies for agency role selector (not needed for agency_admin — backend auto-sets)
  useEffect(() => {
    if (!isAgencyRole || isCurrentUserAgencyAdmin) return;
    const fetchAgencies = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/admin/agencies?status=active`, { headers: getAuthHeaders() });
        if (response.ok) {
          const data = await response.json();
          setCreateAgencies(data.agencies || []);
        }
      } catch {}
    };
    fetchAgencies();
  }, [isAgencyRole, isCurrentUserAgencyAdmin, getAuthHeaders]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_BASE}/api/admin/users`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        onSuccess();
      } else {
        const data = await response.json();
        setError(data.detail || t('users.errCreateUser'));
      }
    } catch (err) {
      setError(t('common.networkShort'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="users-modal-overlay" onClick={onClose}>
      <div className="users-modal" onClick={e => e.stopPropagation()}>
        <div className="users-modal-header">
          <h2>{t('users.createUser')}</h2>
          <button className="users-modal-close" onClick={onClose}>&times;</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="users-modal-body">
            {error && <div className="users-modal-error">{error}</div>}

            <div className="users-form-row">
              <div className="users-form-field">
                <label>{t('auth.firstName')}</label>
                <input
                  type="text"
                  value={formData.first_name}
                  onChange={e => setFormData({ ...formData, first_name: e.target.value })}
                  required
                />
              </div>
              <div className="users-form-field">
                <label>{t('auth.lastName')}</label>
                <input
                  type="text"
                  value={formData.last_name}
                  onChange={e => setFormData({ ...formData, last_name: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="users-form-field">
              <label>{t('auth.email')}</label>
              <input
                type="email"
                value={formData.email}
                onChange={e => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </div>

            <div className="users-form-field">
              <label>{t('login.password')}</label>
              <input
                type="password"
                value={formData.password}
                onChange={e => setFormData({ ...formData, password: e.target.value })}
                required
                minLength={8}
              />
            </div>

            <div className="users-form-field">
              <label>{t('users.role')}</label>
              <select
                value={formData.role}
                onChange={e => {
                  const newRole = e.target.value;
                  setFormData({
                    ...formData,
                    role: newRole,
                    advertiser_ids: (newRole === 'smb_client' || newRole === 'freelancer') ? [] : formData.advertiser_ids,
                    domain: newRole === 'smb_client' ? formData.domain : '',
                    company_name: newRole === 'smb_client' ? formData.company_name : '',
                    agency_id: (newRole === 'smb_client' || newRole === 'freelancer') ? null : formData.agency_id,
                    parent_user_id: (newRole === 'smb_client' || newRole === 'freelancer') ? formData.parent_user_id : null,
                  });
                  if (newRole !== 'smb_client') setDomainTouched(false);
                }}
              >
                {getAllowedRoles(currentUserRole).map(role => (
                  <option key={role.value} value={role.value}>{roleLabel(role.value)}</option>
                ))}
              </select>
            </div>

            {isFreelancerRole ? (
              <>
                <div className="users-form-field">
                  <label>{t('users.regionalOfficeReq')}</label>
                  <OfficePicker
                    offices={offices}
                    selectedId={formData.regional_office_id}
                    onChange={(id) => setFormData({ ...formData, regional_office_id: id, parent_user_id: null })}
                    showBlockWarning={false}
                  />
                </div>

                <div className="users-form-field">
                  <label>{t('users.manager')}</label>
                  <select
                    value={formData.parent_user_id || ''}
                    onChange={e => setFormData({ ...formData, parent_user_id: e.target.value ? Number(e.target.value) : null })}
                  >
                    <option value="">{t('users.notAssigned')}</option>
                    {smbManagers.map(m => (
                      <option key={m.id} value={m.id}>
                        {m.last_name ? `${m.last_name} ${m.first_name ? m.first_name.charAt(0) + '.' : ''}`.trim() : m.email}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="users-form-field">
                  <div className="users-admin-access-notice">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                      <path d="M8 5v3M8 10v1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                    <span>{t('users.freelancerOwnClients')}</span>
                  </div>
                </div>
              </>
            ) : isSmbClient ? (
              <>
                <div className="users-form-field">
                  <label>{t('auth.companyName')}</label>
                  <input
                    type="text"
                    value={formData.company_name}
                    onChange={e => setFormData({ ...formData, company_name: e.target.value })}
                    placeholder={t('users.clientCompanyPh')}
                  />
                </div>

                <div className="users-form-field">
                  <label>{t('clients.domain')}</label>
                  <input
                    type="text"
                    value={formData.domain}
                    onChange={e => { setFormData({ ...formData, domain: e.target.value }); setDomainTouched(true); }}
                    onBlur={() => setDomainTouched(true)}
                    placeholder="https://company.com"
                    style={domainTouched && formData.domain && (() => { try { if (!formData.domain.startsWith('http://') && !formData.domain.startsWith('https://')) return true; new URL(formData.domain); return false; } catch { return true; } })() ? { borderColor: '#e74c3c' } : undefined}
                  />
                  {(() => {
                    // Same two messages the client card uses, so a domain typed in
                    // either place is rejected with the same words.
                    const domainError = (): string | null => {
                      if (!formData.domain.startsWith('http://') && !formData.domain.startsWith('https://')) {
                        return t('clients.errDomainScheme');
                      }
                      try { new URL(formData.domain); return null; } catch { return t('clients.errDomainUrl'); }
                    };
                    const err = domainTouched && formData.domain ? domainError() : null;
                    return err && (
                      <small style={{ color: '#e74c3c', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                        {err}
                      </small>
                    );
                  })()}
                </div>

                <div className="users-form-field">
                  <label>{t('users.regionalOfficeReq')}</label>
                  <OfficePicker
                    offices={offices}
                    selectedId={formData.regional_office_id}
                    onChange={(id) => setFormData({ ...formData, regional_office_id: id, parent_user_id: null })}
                    showBlockWarning={false}
                  />
                </div>

                <div className="users-form-field">
                  <label>{t('users.manager')}</label>
                  <select
                    value={formData.parent_user_id || ''}
                    onChange={e => setFormData({ ...formData, parent_user_id: e.target.value ? Number(e.target.value) : null })}
                  >
                    <option value="">{t('users.notAssigned')}</option>
                    {smbManagers.map(m => (
                      <option key={m.id} value={m.id}>
                        {m.last_name ? `${m.last_name} ${m.first_name ? m.first_name.charAt(0) + '.' : ''}`.trim() : m.email}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="users-form-field">
                  <div className="users-admin-access-notice">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                      <path d="M8 5v3M8 10v1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                    <span>A new client will be created automatically for this user</span>
                  </div>
                </div>
              </>
            ) : isAdminRole ? (
              <div className="users-form-field">
                <label>{t('users.colClients')}</label>
                <div className="users-admin-access-notice">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M8 1L1 4v4c0 4.42 2.98 7.73 7 8 4.02-.27 7-3.58 7-8V4L8 1z" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                    <path d="M5 8l2 2 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span>{t('users.adminAllClients')}</span>
                </div>
              </div>
            ) : isRegionalAdmin ? (
              <div className="users-form-field">
                <label>{t('clients.regionalOffice')}</label>
                <OfficePicker
                  offices={offices}
                  selectedId={formData.regional_office_id}
                  onChange={(id) => setFormData({ ...formData, regional_office_id: id })}
                />
              </div>
            ) : (
              <>
                {needsOffice && !isCurrentUserAgencyAdmin && (
                  <div className="users-form-field">
                    <label>{t('clients.regionalOffice')}</label>
                    <OfficePicker
                      offices={offices}
                      selectedId={formData.regional_office_id}
                      onChange={(id) => setFormData({ ...formData, regional_office_id: id, advertiser_ids: [] })}
                      showBlockWarning={false}
                    />
                  </div>
                )}
                {isAgencyRole && !isCurrentUserAgencyAdmin && (
                  <div className="users-form-field">
                    <label>{t('users.agencyReq')}</label>
                    <select
                      value={formData.agency_id || ''}
                      onChange={e => setFormData({ ...formData, agency_id: e.target.value ? Number(e.target.value) : null })}
                      required
                    >
                      <option value="">{t('users.selectAgency')}</option>
                      {createAgencies.map(a => (
                        <option key={a.id} value={a.id}>{a.name}</option>
                      ))}
                    </select>
                  </div>
                )}
                {isAgencyRole && !['agency_manager', 'agency_buyer', 'agency_analyst'].includes(formData.role) ? (
                  <div className="users-form-field">
                    <label>{t('users.colClients')}</label>
                    <div className="users-admin-access-notice">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M8 1L1 4v4c0 4.42 2.98 7.73 7 8 4.02-.27 7-3.58 7-8V4L8 1z" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                        <path d="M5 8l2 2 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span>{t('users.agencyAllClients')}</span>
                    </div>
                  </div>
                ) : isAgencyRole && ['agency_manager', 'agency_buyer', 'agency_analyst'].includes(formData.role) ? (
                  <div className="users-form-field">
                    <label>{t('users.colClients')}</label>
                    <AdvertiserPicker
                      advertisers={advertisers}
                      selectedIds={formData.advertiser_ids}
                      onChange={(ids) => setFormData({ ...formData, advertiser_ids: ids })}
                    />
                  </div>
                ) : needsOffice && !formData.regional_office_id ? (
                  <div className="users-form-field">
                    <label>{t('users.colClients')}</label>
                    <div className="users-admin-access-notice">
                      <span>{t('users.selectOfficeFirst')}</span>
                    </div>
                  </div>
                ) : (
                  <div className="users-form-field">
                    <label>{t('users.colClients')}</label>
                    {loadingAdvertisers ? (
                      <div className="users-admin-access-notice"><span>{t('users.loadingClients')}</span></div>
                    ) : (
                      <AdvertiserPicker
                        advertisers={needsOffice ? officeAdvertisers : advertisers}
                        selectedIds={formData.advertiser_ids}
                        onChange={(ids) => setFormData({ ...formData, advertiser_ids: ids })}
                      />
                    )}
                  </div>
                )}
              </>
            )}
          </div>
          <div className="users-modal-footer">
            <button type="button" className="users-btn users-btn-secondary" onClick={onClose}>
              {t('common.cancel')}
            </button>
            <button type="submit" className="users-btn users-btn-primary" disabled={isSubmitting}>
              {t(isSubmitting ? 'clients.creating' : 'users.createUser')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Invite User Modal
interface InviteUserModalProps {
  onClose: () => void;
  onSuccess: () => void;
  advertisers: Advertiser[];
  offices: Office[];
  getAuthHeaders: () => Record<string, string>;
  currentUserRole?: string;
}

const InviteUserModal: React.FC<InviteUserModalProps> = ({ onClose, onSuccess, advertisers, offices, getAuthHeaders, currentUserRole }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    email: '',
    role: 'analyst',
    advertiser_ids: [] as string[],
    expires_days: 7,
    regional_office_id: null as number | null,
  });
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [inviteLink, setInviteLink] = useState('');
  const [officeAdvertisers, setOfficeAdvertisers] = useState<Advertiser[]>([]);
  const [loadingAdvertisers, setLoadingAdvertisers] = useState(false);

  // Fetch advertisers filtered by office when office changes (for manager/analyst)
  useEffect(() => {
    const needsOfficeFilter = ['analyst', 'manager'].includes(formData.role);
    if (!needsOfficeFilter || !formData.regional_office_id) {
      setOfficeAdvertisers([]);
      return;
    }
    const fetchOfficeAdvertisers = async () => {
      setLoadingAdvertisers(true);
      try {
        const response = await fetch(
          `${API_BASE}/api/advertisers?regional_office_id=${formData.regional_office_id}&active_only=true`,
          { headers: getAuthHeaders() }
        );
        if (response.ok) {
          const data = await response.json();
          setOfficeAdvertisers((data.advertisers || []).map((a: any) => ({
            id: a.advertiser_id,
            name: a.display_name,
          })));
        }
      } catch (err) {
        console.error('Failed to fetch office advertisers:', err);
      } finally {
        setLoadingAdvertisers(false);
      }
    };
    fetchOfficeAdvertisers();
  }, [formData.regional_office_id, formData.role, getAuthHeaders]);

  // Close on Esc
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isSubmitting) onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose, isSubmitting]);

  const isAdminRole = formData.role === 'admin';
  const isRegionalAdmin = formData.role === 'regional_admin';
  const needsOffice = ['analyst', 'manager'].includes(formData.role);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_BASE}/api/admin/users/invite`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        const data = await response.json();
        setInviteLink(`${window.location.origin}/auth/accept-invite?token=${data.token}`);
      } else {
        const data = await response.json();
        setError(data.detail || t('users.errCreateInvitation'));
      }
    } catch (err) {
      setError(t('common.networkShort'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(inviteLink);
  };

  return (
    <div className="users-modal-overlay" onClick={onClose}>
      <div className="users-modal" onClick={e => e.stopPropagation()}>
        <div className="users-modal-header">
          <h2>{t('users.sendInvitation')}</h2>
          <button className="users-modal-close" onClick={onClose}>&times;</button>
        </div>
        {inviteLink ? (
          <div className="users-modal-body">
            <div className="users-invite-success">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                <polyline points="22 4 12 14.01 9 11.01"/>
              </svg>
              <h3>{t('users.invitationSent')}</h3>
              <p>{t('users.shareLink')}</p>
              <div className="users-invite-link">
                <input type="text" value={inviteLink} readOnly />
                <button className="users-btn users-btn-primary" onClick={copyLink}>{t('clients.copy')}</button>
              </div>
            </div>
            <div className="users-modal-footer">
              <button className="users-btn users-btn-primary" onClick={onSuccess}>{t('billing.done')}</button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="users-modal-body">
              {error && <div className="users-modal-error">{error}</div>}

              <div className="users-form-field">
                <label>{t('auth.email')}</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={e => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>

              <div className="users-form-field">
                <label>{t('users.role')}</label>
                <select
                  value={formData.role}
                  onChange={e => setFormData({ ...formData, role: e.target.value })}
                >
                  {getAllowedRoles(currentUserRole).map(role => (
                    <option key={role.value} value={role.value}>{roleLabel(role.value)}</option>
                  ))}
                </select>
              </div>

              <div className="users-form-field">
                <label>{t('users.expiresIn')}</label>
                <select
                  value={formData.expires_days}
                  onChange={e => setFormData({ ...formData, expires_days: parseInt(e.target.value) })}
                >
                  {[3, 7, 14, 30].map(d => (
                    <option key={d} value={d}>{t('users.daysCount', { count: d })}</option>
                  ))}
                </select>
              </div>

              {isAdminRole ? (
                <div className="users-form-field">
                  <label>{t('users.colClients')}</label>
                  <div className="users-admin-access-notice">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M8 1L1 4v4c0 4.42 2.98 7.73 7 8 4.02-.27 7-3.58 7-8V4L8 1z" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                      <path d="M5 8l2 2 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span>{t('users.adminAllClients')}</span>
                  </div>
                </div>
              ) : isRegionalAdmin ? (
                <div className="users-form-field">
                  <label>{t('clients.regionalOffice')}</label>
                  <OfficePicker
                    offices={offices}
                    selectedId={formData.regional_office_id}
                    onChange={(id) => setFormData({ ...formData, regional_office_id: id })}
                  />
                </div>
              ) : (
                <>
                  {needsOffice && (
                    <div className="users-form-field">
                      <label>{t('clients.regionalOffice')}</label>
                      <OfficePicker
                        offices={offices}
                        selectedId={formData.regional_office_id}
                        onChange={(id) => setFormData({ ...formData, regional_office_id: id, advertiser_ids: [] })}
                        showBlockWarning={false}
                      />
                    </div>
                  )}
                  {needsOffice && !formData.regional_office_id ? (
                    <div className="users-form-field">
                      <label>{t('users.preassignClients')}</label>
                      <div className="users-admin-access-notice">
                        <span>{t('users.selectOfficeFirst')}</span>
                      </div>
                    </div>
                  ) : (
                    <div className="users-form-field">
                      <label>{t('users.preassignClients')}</label>
                      {loadingAdvertisers ? (
                        <div className="users-admin-access-notice"><span>{t('users.loadingClients')}</span></div>
                      ) : (
                        <AdvertiserPicker
                          advertisers={needsOffice ? officeAdvertisers : advertisers}
                          selectedIds={formData.advertiser_ids}
                          onChange={(ids) => setFormData({ ...formData, advertiser_ids: ids })}
                        />
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="users-modal-footer">
              <button type="button" className="users-btn users-btn-secondary" onClick={onClose}>
                {t('common.cancel')}
              </button>
              <button type="submit" className="users-btn users-btn-primary" disabled={isSubmitting}>
                {t(isSubmitting ? 'users.sending' : 'users.sendInvitation')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

// Edit User Modal
interface EditUserModalProps {
  user: User;
  onClose: () => void;
  onSuccess: () => void;
  advertisers: Advertiser[];
  offices: Office[];
  getAuthHeaders: () => Record<string, string>;
  currentUserRole?: string;
}

const EditUserModal: React.FC<EditUserModalProps> = ({ user, onClose, onSuccess, advertisers, offices, getAuthHeaders, currentUserRole }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    first_name: user.first_name,
    last_name: user.last_name,
    role: user.role,
    regional_office_id: user.regional_office_id,
    parent_user_id: user.parent_user_id,
    billing_model: user.billing_model || 'prepaid',
  });
  const [selectedAdvertisers, setSelectedAdvertisers] = useState<string[]>(
    (user.advertisers || []).map(a => a.id)
  );
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [passwordSaved, setPasswordSaved] = useState(false);
  const [isSavingPassword, setIsSavingPassword] = useState(false);
  const [officeAdvertisers, setOfficeAdvertisers] = useState<Advertiser[]>([]);
  const [loadingAdvertisers, setLoadingAdvertisers] = useState(false);
  const [editManagers, setEditManagers] = useState<{ id: number; email: string; first_name: string | null; last_name: string | null }[]>([]);

  // Fetch advertisers filtered by office when office changes (for manager/analyst)
  useEffect(() => {
    const needsOfficeFilter = ['analyst', 'manager'].includes(formData.role);
    if (!needsOfficeFilter || !formData.regional_office_id) {
      setOfficeAdvertisers([]);
      return;
    }
    const fetchOfficeAdvertisers = async () => {
      setLoadingAdvertisers(true);
      try {
        const response = await fetch(
          `${API_BASE}/api/advertisers?regional_office_id=${formData.regional_office_id}&active_only=true`,
          { headers: getAuthHeaders() }
        );
        if (response.ok) {
          const data = await response.json();
          setOfficeAdvertisers((data.advertisers || []).map((a: any) => ({
            id: a.advertiser_id,
            name: a.display_name,
          })));
        }
      } catch (err) {
        console.error('Failed to fetch office advertisers:', err);
      } finally {
        setLoadingAdvertisers(false);
      }
    };
    fetchOfficeAdvertisers();
  }, [formData.regional_office_id, formData.role, getAuthHeaders]);

  // Fetch managers for smb_client/freelancer
  const isSmbClient = user.role === 'smb_client';
  const isFreelancerRole = user.role === 'freelancer';
  useEffect(() => {
    if ((!isSmbClient && !isFreelancerRole) || !formData.regional_office_id) {
      setEditManagers([]);
      return;
    }
    const fetchManagers = async () => {
      try {
        const response = await fetch(
          `${API_BASE}/api/admin/clients/managers?office_id=${formData.regional_office_id}`,
          { headers: getAuthHeaders() }
        );
        if (response.ok) {
          const data = await response.json();
          setEditManagers(data.managers || []);
        }
      } catch {}
    };
    fetchManagers();
  }, [isSmbClient, isFreelancerRole, formData.regional_office_id, getAuthHeaders]);

  // Close on Esc
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isSubmitting) onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose, isSubmitting]);

  // Admin roles have access to all advertisers
  const isAdminRole = formData.role === 'admin';
  const isRegionalAdmin = formData.role === 'regional_admin';
  const needsOffice = ['analyst', 'manager'].includes(formData.role);
  const showOfficeForSelfManaged = isSmbClient || isFreelancerRole;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      // Update user info
      const userResponse = await fetch(`${API_BASE}/api/admin/users/${user.id}`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify(formData),
      });

      if (!userResponse.ok) {
        const data = await userResponse.json();
        throw new Error(data.detail || t('users.errUpdateUser'));
      }

      // Update advertisers (skip for admin and smb_client)
      if (!isAdminRole && !isSmbClient) {
        const advResponse = await fetch(`${API_BASE}/api/admin/users/${user.id}/advertisers`, {
          method: 'PUT',
          headers: getAuthHeaders(),
          body: JSON.stringify({ advertiser_ids: selectedAdvertisers }),
        });

        if (!advResponse.ok) {
          const data = await advResponse.json();
          throw new Error(data.detail || t('users.errUpdateAdvertisers'));
        }
      }

      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('users.errUpdatingUser'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChangePassword = async () => {
    if (newPassword.length < 6) {
      setError(t('users.errPassword6'));
      return;
    }
    setIsSavingPassword(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE}/api/admin/users/${user.id}/change-password`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ password: newPassword }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || t('users.errChangePassword'));
      setPasswordSaved(true);
      setTimeout(() => setPasswordSaved(false), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('users.errChangingPassword'));
    } finally {
      setIsSavingPassword(false);
    }
  };

  return (
    <div className="users-modal-overlay" onClick={onClose}>
      <div className="users-modal" onClick={e => e.stopPropagation()}>
        <div className="users-modal-header">
          <h2>{t('users.editUser')}</h2>
          <button className="users-modal-close" onClick={onClose}>&times;</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="users-modal-body">
            {error && <div className="users-modal-error">{error}</div>}

            <div className="users-form-row">
              <div className="users-form-field">
                <label>{t('auth.firstName')}</label>
                <input
                  type="text"
                  value={formData.first_name}
                  onChange={e => setFormData({ ...formData, first_name: e.target.value })}
                  required
                />
              </div>
              <div className="users-form-field">
                <label>{t('auth.lastName')}</label>
                <input
                  type="text"
                  value={formData.last_name}
                  onChange={e => setFormData({ ...formData, last_name: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="users-form-field">
              <label>{t('auth.email')}</label>
              <input type="email" value={user.email} disabled />
            </div>

            <div className="users-form-field">
              <label>{t('users.role')}</label>
              {isSmbClient ? (
                <input type="text" value={roleLabel('smb_client')} disabled />
              ) : (
                <select
                  value={formData.role}
                  onChange={e => setFormData({ ...formData, role: e.target.value })}
                >
                  {getAllowedRoles(currentUserRole).map(role => (
                    <option key={role.value} value={role.value}>{roleLabel(role.value)}</option>
                  ))}
                </select>
              )}
            </div>

            {showOfficeForSelfManaged && currentUserRole === 'admin' && (
              <div className="users-form-field">
                <label>{t('clients.regionalOffice')}</label>
                <OfficePicker
                  offices={offices}
                  selectedId={formData.regional_office_id}
                  onChange={(id) => setFormData({ ...formData, regional_office_id: id, parent_user_id: null })}
                  showBlockWarning={false}
                  includeHub
                />
                <span style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4, display: 'block' }}>
                  {t('users.officeMovesClients')}
                </span>
              </div>
            )}

            {showOfficeForSelfManaged && (
              <div className="users-form-field">
                <label>{t('users.brandManager', { brand: getBranding().brand_name })}</label>
                <select
                  value={formData.parent_user_id || ''}
                  onChange={e => setFormData({ ...formData, parent_user_id: e.target.value ? Number(e.target.value) : null })}
                >
                  <option value="">{t('users.notAssigned')}</option>
                  {editManagers.map(m => (
                    <option key={m.id} value={m.id}>
                      {m.last_name ? `${m.last_name} ${m.first_name ? m.first_name.charAt(0) + '.' : ''}`.trim() : m.email}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {showOfficeForSelfManaged && (
              <div className="users-form-field">
                <label>{t('clients.billingModel')}</label>
                <select value={formData.billing_model} onChange={e => setFormData({ ...formData, billing_model: e.target.value })}>
                  <option value="prepaid">{t('billing.bm_prepaid')}</option>
                  <option value="postpaid">{t('billing.bm_postpaid')}</option>
                </select>
              </div>
            )}

            {isSmbClient ? (
              <div className="users-form-field">
                <label>{t('users.client')}</label>
                <input
                  type="text"
                  value={(user.advertisers || []).map(a => a.name).join(', ') || t('users.noClientAssigned')}
                  disabled
                />
              </div>
            ) : isAdminRole ? (
              <div className="users-form-field">
                <label>{t('users.colClients')}</label>
                <div className="users-admin-access-notice">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M8 1L1 4v4c0 4.42 2.98 7.73 7 8 4.02-.27 7-3.58 7-8V4L8 1z" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                    <path d="M5 8l2 2 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span>{t('users.adminAllClients')}</span>
                </div>
              </div>
            ) : isRegionalAdmin ? (
              <div className="users-form-field">
                <label>{t('clients.regionalOffice')}</label>
                <OfficePicker
                  offices={offices}
                  selectedId={formData.regional_office_id}
                  onChange={(id) => setFormData({ ...formData, regional_office_id: id })}
                />
              </div>
            ) : (
              <>
                {needsOffice && (
                  <div className="users-form-field">
                    <label>{t('clients.regionalOffice')}</label>
                    <OfficePicker
                      offices={offices}
                      selectedId={formData.regional_office_id}
                      onChange={(id) => { setFormData({ ...formData, regional_office_id: id }); setSelectedAdvertisers([]); }}
                      showBlockWarning={false}
                    />
                  </div>
                )}
                {needsOffice && !formData.regional_office_id ? (
                  <div className="users-form-field">
                    <label>{t('users.colClients')}</label>
                    <div className="users-admin-access-notice">
                      <span>{t('users.selectOfficeFirst')}</span>
                    </div>
                  </div>
                ) : (
                  <div className="users-form-field">
                    <label>{t('users.colClients')}</label>
                    {loadingAdvertisers ? (
                      <div className="users-admin-access-notice"><span>{t('users.loadingClients')}</span></div>
                    ) : (
                      <AdvertiserPicker
                        advertisers={needsOffice ? officeAdvertisers : advertisers}
                        selectedIds={selectedAdvertisers}
                        onChange={setSelectedAdvertisers}
                      />
                    )}
                  </div>
                )}
              </>
            )}

            <div className="users-form-field">
              <label>{t('users.accountInfo')}</label>
              <div className="users-account-info">
                <div>
                  <span className="users-account-label">{t('users.statusLabel')}</span>
                  <span
                    className="users-status-badge"
                    style={{ backgroundColor: STATUS_LABELS[user.status]?.color || '#6c757d' }}
                  >
                    {statusLabel(user.status)}
                  </span>
                </div>
                <div>
                  <span className="users-account-label">{t('users.twofaLabel')}</span>
                  <span>{t(user.totp_enabled ? 'users.enabled' : 'users.disabled')}</span>
                </div>
                <div>
                  <span className="users-account-label">{t('users.createdLabel')}</span>
                  <span>{new Date(user.created_at).toLocaleDateString(currentLocale())}</span>
                </div>
              </div>
            </div>

            <div className="users-form-field">
              <label>{t('users.changePassword')}</label>
              <div className="users-password-change">
                <input
                  type="password"
                  placeholder={t('users.newPasswordPh')}
                  value={newPassword}
                  onChange={e => { setNewPassword(e.target.value); setPasswordSaved(false); }}
                />
                <button
                  type="button"
                  className="users-btn users-btn-outline"
                  onClick={handleChangePassword}
                  disabled={isSavingPassword || !newPassword}
                >
                  {isSavingPassword ? t('users.saving') : passwordSaved ? t('users.savedBang') : t('common.save')}
                </button>
              </div>
            </div>
          </div>
          <div className="users-modal-footer">
            <button type="button" className="users-btn users-btn-secondary" onClick={onClose}>
              {t('common.cancel')}
            </button>
            <button type="submit" className="users-btn users-btn-primary" disabled={isSubmitting}>
              {t(isSubmitting ? 'users.saving' : 'users.saveChanges')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UsersPage;
